import { ALL_CALCULATIONS } from "@suse-o11y/calculate-value-from-points";
import { filter } from "@suse-o11y/filter";
import { ImmutableSet } from "@suse-o11y/immutable-set";
import { isPresent } from "@suse-o11y/is-present";
import type { Optional } from "@suse-o11y/optional";
import { piped } from "@suse-o11y/piped";
import { prop } from "@suse-o11y/prop";
import { sortBy } from "@suse-o11y/sort-by";
import { z } from "zod";

import {
	ALL_MODES as ALL_LEGEND_MODES,
	ALL_POSITIONS as ALL_LEGEND_POSITIONS,
	type Settings as LegendSettings,
} from "../../../components/widget-form/legend/settings";
import {
	ALL_MODES as ALL_THRESHOLDS_MODES,
	DEFAULT_STEP as DEFAULT_THRESHOLDS_STEP,
	type Step as ThresholdsStep,
} from "../../../components/widget-form/thresholds/settings";
import { dashboardWidgetFormatSchema } from "../_dashboard-widget-format";

const yAxisSchema = z.object({
	show: z.boolean().optional().catch(undefined),
	label: z.string().trim().optional().catch(undefined),
	min: z.number().optional().catch(undefined),
	max: z.number().optional().catch(undefined),
	format: dashboardWidgetFormatSchema.optional().catch(undefined),
});

type YAxis = z.infer<typeof yAxisSchema>;

const YAxis = {
	schema: yAxisSchema,
};

const legendSchema = z.object({
	show: z.boolean().optional().catch(undefined),
	position: z.enum(ALL_LEGEND_POSITIONS).optional().catch(undefined),
	mode: z.enum(ALL_LEGEND_MODES).optional().catch(undefined),
	values: z
		.array(z.enum(ALL_CALCULATIONS).optional().catch(undefined))
		.transform(filter(isPresent))
		.optional()
		.catch(undefined),
});

type Legend = z.infer<typeof legendSchema>;

function legendFromSettings(settings: undefined | LegendSettings): Legend {
	return {
		show: settings?.show,
		position: settings?.position,
		mode: settings?.mode,
		values: settings?.columns?.toArray(),
	};
}

function legendToSettings(spec: undefined | Legend): LegendSettings {
	return {
		show: spec?.show,
		position: spec?.position,
		mode: spec?.mode,
		columns: spec?.values == null ? undefined : ImmutableSet.fromArray(spec.values),
	} satisfies Optional<LegendSettings>;
}

const Legend = {
	schema: legendSchema,
	fromSettings: legendFromSettings,
	toSettings: legendToSettings,
};

const thresholdsStepsSchema = z
	.array(
		z
			.object({
				color: z.string().trim(),
				value: z.number().catch(DEFAULT_THRESHOLDS_STEP.value),
			})
			.optional()
			.catch(undefined),
	)
	.transform(piped(filter(isPresent), sortBy([prop("value"), "desc"])))
	.catch([]);

type ThresholdsSteps = z.infer<typeof thresholdsStepsSchema>;

function thresholdsStepsFromSettings(settings: ReadonlyArray<ThresholdsStep> = []): ThresholdsSteps {
	return settings.map((step) => ({
		value: step.value ?? DEFAULT_THRESHOLDS_STEP.value,
		color: step.color ?? DEFAULT_THRESHOLDS_STEP.color,
	}));
}

const ThresholdsSteps = {
	schema: thresholdsStepsSchema,
	fromSettings: thresholdsStepsFromSettings,
};

const thresholdsModeSchema = z.enum(ALL_THRESHOLDS_MODES);

type ThresholdsMode = z.infer<typeof thresholdsModeSchema>;

const ThresholdsMode = {
	schema: thresholdsModeSchema,
};

export { Legend, ThresholdsMode, ThresholdsSteps, YAxis };
